/**
 * The page a customer opens from the email.
 *
 * No sign-in. The customer is not a user of the app — they hold a link, and the
 * link is the whole of their identity. That means everything here has to work
 * for someone who has never seen this software, on a phone, once.
 *
 * The document is the same `EstimateSheet` the shop sees and prints. Showing a
 * summary instead would mean approving one thing and receiving another.
 */

import { useState } from 'react';
import { EstimateSheet } from '../document/EstimateSheet.js';
import { localDate } from '../intl/dates.js';
import {
  NOTE_LIMIT, linkState, recordResponse, stateMessage, validateResponse,
  type Choice, type LinkInput, type RecordedApproval,
} from './decision.js';
import type { EstimateDocument } from '../document/estimate.js';

interface Props {
  doc: EstimateDocument;
  link: LinkInput;
  /** Shown when the link has already been answered. */
  existing?: RecordedApproval | null;
  onRespond: (recorded: RecordedApproval) => void;
}

const CHOICES: Array<{ id: Choice; label: string; hint: string }> = [
  { id: 'approved', label: 'Approve', hint: 'Go ahead with all of it.' },
  { id: 'partly', label: 'Approve part of it', hint: 'Tell them what to leave out.' },
  { id: 'declined', label: 'Do not approve', hint: 'Nothing goes ahead.' },
];

export function ApprovalPage({ doc, link, existing, onRespond }: Props) {
  const state = linkState(link);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<RecordedApproval | null>(null);

  const submit = () => {
    if (!choice) return;
    const fault = validateResponse({ choice, note });
    if (fault) {
      setProblem(fault === 'note_required'
        ? 'Say what to leave out — "some of it" is not something the shop can act on.'
        : `That is longer than ${NOTE_LIMIT.toLocaleString()} characters.`);
      return;
    }
    setProblem(null);
    const recorded = recordResponse({ choice, note }, {
      version: doc.issued?.version ?? 1,
      hash: doc.issued?.hash ?? '',
    }, new Date().toISOString());
    setDone(recorded);
    onRespond(recorded);
  };

  const answered = done ?? (state === 'already_answered' ? existing ?? null : null);
  const message = stateMessage(state);

  return (
    <div className="approve" dir={doc.language === 'ar' ? 'rtl' : 'ltr'} lang={doc.language}>
      <header className="approve-h">
        <b><bdi>{doc.shopName}</bdi></b>
        <span className="muted small">Estimate {doc.reference}</span>
      </header>

      {message.title && (
        <div className={`note ${state === 'already_answered' ? 'good' : 'warn'}`}>
          <b>{message.title}</b>
          {message.body}
        </div>
      )}

      <EstimateSheet doc={doc} />

      {answered ? (
        <div className={`note ${answered.choice === 'approved' ? 'good'
          : answered.choice === 'declined' ? 'bad' : 'warn'}`}>
          <b>
            {answered.choice === 'approved' ? '✓ You approved this'
              : answered.choice === 'partly' ? 'You approved part of this'
              : 'You did not approve this'}
          </b>
          <p>
            Recorded against version {answered.version}
            {answered.hash && <> · <span className="mono">{answered.hash}</span></>}
            {' '}on {localDate(answered.at, doc.language)}.
          </p>
          {answered.note && <p>You wrote: “<bdi>{answered.note}</bdi>”</p>}
          {answered.choice === 'partly' && (
            <p className="small">
              The shop will send a revised estimate. That one needs its own approval —
              this answer does not carry over to it.
            </p>
          )}
        </div>
      ) : state !== 'ready' ? null : (
        <section className="approve-form">
          <h2>What would you like to do?</h2>

          <div className="choices">
            {CHOICES.map((c) => (
              <label key={c.id} className={`choice ${choice === c.id ? 'on' : ''}`}>
                <input type="radio" name="choice" value={c.id}
                       checked={choice === c.id}
                       onChange={() => { setChoice(c.id); setProblem(null); }} />
                <span className="ttl">{c.label}</span>
                <span className="hint">{c.hint}</span>
              </label>
            ))}
          </div>

          {choice === 'partly' && (
            <label className="field wide">
              <span>What should they leave out?</span>
              <textarea rows={4} value={note} maxLength={NOTE_LIMIT}
                        placeholder="Go ahead with the bumper and headlamp, but leave the fender for now."
                        onChange={(e) => { setNote(e.target.value); setProblem(null); }} />
            </label>
          )}

          {choice && choice !== 'partly' && (
            <label className="field wide">
              <span>Anything you want to add? (optional)</span>
              <textarea rows={2} value={note} maxLength={NOTE_LIMIT}
                        onChange={(e) => setNote(e.target.value)} />
            </label>
          )}

          {problem && <div className="note warn"><b>Almost</b>{problem}</div>}

          <button className="btn primary" disabled={!choice} onClick={submit}>
            Send my answer
          </button>

          <p className="muted small">
            Your answer is recorded against this exact estimate. If the shop changes it
            afterwards, they have to ask you again.
          </p>
        </section>
      )}

      <footer className="approve-f muted small">
        Sent by <bdi>{doc.shopName}</bdi>. If you were not expecting this, you can ignore it —
        nothing happens until you answer.
      </footer>
    </div>
  );
}
